import { useState, useRef } from "react";
import { motion, type PanInfo } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { hapticFeedback } from "../lib/telegram";
import { useBrandName } from "../lib/company";

interface Card {
  id: string;
  card_name: string;
  status: string;
  type: "physical" | "virtual";
  lastFour?: string;
  balance: number;
  limit?: number;
  expiry?: string;
}

interface CardCarouselProps {
  cards: Card[];
}

// The card object is the ONLY dark thing in the app (DESIGN.md) — physical
// and virtual share the same deep teal-black; the mono label tells them apart.
const CARD_GRADIENT = "from-[#12312C] via-card-obj to-[#071512]";

const statusStamp = (status: string) => {
  const colors: Record<string, string> = {
    active: "text-mint",
    suspended: "text-warning",
    cancelled: "text-error",
  };
  return colors[status.toLowerCase()] || "text-[#9DBDB6]";
};

export const CardCarousel = ({ cards }: CardCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isDragging = useRef(false);
  const navigate = useNavigate();
  const brandName = useBrandName();

  if (cards.length === 0) {
    return (
      <div className="bg-surface rounded-xl border border-hairline p-8 text-center">
        <p className="text-muted">No cards yet</p>
      </div>
    );
  }

  const goTo = (index: number) => {
    if (index >= 0 && index < cards.length && index !== currentIndex) {
      setCurrentIndex(index);
      hapticFeedback.selectionChanged();
    }
  };

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 30;
    const velocity = info.velocity.x;
    // Use velocity for quick flicks, offset for slow drags
    if ((info.offset.x < -threshold || velocity < -300) && currentIndex < cards.length - 1) {
      goTo(currentIndex + 1);
    } else if ((info.offset.x > threshold || velocity > 300) && currentIndex > 0) {
      goTo(currentIndex - 1);
    }
    // Delay resetting drag flag so the click handler can check it
    setTimeout(() => { isDragging.current = false; }, 50);
  };

  const handleCardTap = () => {
    if (isDragging.current) return;
    const card = cards[currentIndex];
    hapticFeedback.light();
    navigate(`/card/${card.id}?type=${card.type}`);
  };

  const hasMultiple = cards.length > 1;

  return (
    <div className="relative">
      {/* Cards track — show peek of adjacent cards */}
      <div className={`overflow-hidden ${hasMultiple ? "-mx-2 px-2" : ""}`}>
        <motion.div
          className="flex"
          style={{ gap: hasMultiple ? 12 : 0 }}
          animate={{ x: hasMultiple ? `calc(-${currentIndex * 100}% - ${currentIndex * 12}px)` : "0%" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          drag={hasMultiple ? "x" : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.15}
          onDragStart={() => { isDragging.current = true; }}
          onDragEnd={handleDragEnd}
        >
          {cards.map((card, i) => (
            <motion.div
              key={card.id}
              className={`card-shimmer flex-shrink-0 aspect-[1.7/1] rounded-[14px] p-4 bg-gradient-to-br ${CARD_GRADIENT} text-[#E8F5F2] cursor-pointer shadow-[0_18px_40px_rgba(11,31,28,0.35)] flex flex-col justify-between overflow-hidden relative transition-opacity duration-200`}
              style={{ width: hasMultiple ? "calc(100% - 16px)" : "100%", opacity: i === currentIndex ? 1 : 0.5 }}
              onClick={handleCardTap}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex justify-between items-start relative z-10">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">
                    {card.type === "physical" ? "Physical" : "Virtual"}
                  </p>
                  <p className="text-sm font-semibold">{card.card_name}</p>
                </div>
                <span className={`stamp ${statusStamp(card.status)}`}>
                  {card.status.toLowerCase()}
                </span>
              </div>

              <p className="font-mono text-sm tracking-[0.14em] text-[#9DBDB6]/70 relative z-10">
                •••• •••• •••• {card.lastFour || "••••"}
              </p>

              <div className="flex justify-between items-end relative z-10">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">
                    Balance
                  </p>
                  <p className="money text-lg font-semibold">${card.balance.toFixed(2)}</p>
                </div>
                {card.expiry && (
                  <div className="text-center">
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">
                      Exp
                    </p>
                    <p className="font-mono text-xs">{card.expiry}</p>
                  </div>
                )}
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#9DBDB6]/60">
                  {brandName}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Tappable dots indicator */}
      {hasMultiple && (
        <div className="flex justify-center gap-2 mt-3">
          {cards.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="p-1"
              aria-label={`Go to card ${i + 1}`}
            >
              <div
                className={`rounded-full transition-all duration-200 ${
                  i === currentIndex
                    ? "w-4 h-1.5 bg-accent"
                    : "w-1.5 h-1.5 bg-hairline"
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
